import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { reactPost, comment } from "../../../functions/post";
import Moment from "react-moment";
import "moment/locale/pt-br";

export default function YouTubePlayer({ post, onClose }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [liked, setLiked] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [commentText, setCommentText] = useState("");

  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const videoUrl = post.videos?.[0]?.url || post.images?.[0]?.url;
  const youtubeData = post.youtubeData || {};

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play();
    }
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (playing) setShowControls(false);
    }, 3000);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percent * duration;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const toggleFullscreen = () => {
    if (!fullscreen) {
      if (playerRef.current.requestFullscreen) {
        playerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setFullscreen(!fullscreen);
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleLike = async () => {
    await reactPost(post._id, "like", user.token);
    setLiked(!liked);
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    await comment(post._id, commentText, user.token);
    setCommentText("");
  };

  return (
    <div className="youtube_player_overlay">
      <div className="youtube_player_container" ref={playerRef}>
        {/* Video Player */}
        <div
          className="youtube_video_wrapper"
          onMouseMove={handleMouseMove}
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setPlaying(false)}
          />

          {/* Controls Overlay */}
          <div className={`youtube_controls ${showControls ? "visible" : ""}`}>
            <div className="youtube_progress" onClick={handleSeek}>
              <div
                className="youtube_progress_bar"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              {youtubeData.chapters?.map((chapter, i) => (
                <div
                  key={i}
                  className="youtube_chapter_marker"
                  style={{ left: `${(chapter.startTime / duration) * 100}%` }}
                  title={chapter.title}
                />
              ))}
            </div>

            <div className="youtube_controls_bottom">
              <div className="youtube_controls_left">
                <button onClick={togglePlay}>
                  {playing ? (
                    <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>

                <div className="youtube_volume">
                  <button onClick={toggleMute}>
                    {muted || volume === 0 ? (
                      <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                      </svg>
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={muted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="youtube_volume_slider"
                  />
                </div>

                <span className="youtube_time">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              <div className="youtube_controls_right">
                <button onClick={toggleFullscreen}>
                  <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Panel */}
        <div className="youtube_info_panel">
          <button className="youtube_close_btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>

          <h1 className="youtube_video_title">
            {youtubeData.title || post.text || "Vídeo sem título"}
          </h1>

          <div className="youtube_video_stats">
            <span>{post.shares?.length || 0} visualizações</span>
            <span>•</span>
            <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
          </div>

          <div className="youtube_actions_bar">
            <button className={`youtube_action_btn ${liked ? "active" : ""}`} onClick={handleLike}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
              </svg>
              <span>{liked ? "Curtiu" : "Curtir"}</span>
            </button>

            <button className="youtube_action_btn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
              </svg>
              <span>Compartilhar</span>
            </button>

            <button className="youtube_action_btn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z" />
              </svg>
              <span>Salvar</span>
            </button>
          </div>

          <div className="youtube_channel_info">
            <Link to={`/profile/${post.user.username}`}>
              <img src={post.user.picture} alt="" className="youtube_channel_pic" />
            </Link>
            <div className="youtube_channel_details">
              <Link to={`/profile/${post.user.username}`} className="youtube_channel_link">
                {post.user.first_name} {post.user.last_name}
              </Link>
              <span className="youtube_subscriber_count">
                {post.user.followers?.length || 0} seguidores
              </span>
            </div>
            <button className="youtube_subscribe_btn">Seguir</button>
          </div>

          {/* Description */}
          <div
            className={`youtube_description ${showDescription ? "expanded" : ""}`}
            onClick={() => setShowDescription(!showDescription)}
          >
            <p>{youtubeData.description || post.text}</p>
            {youtubeData.tags?.length > 0 && (
              <div className="youtube_tags">
                {youtubeData.tags.map((tag, i) => (
                  <span key={i}>#{tag}</span>
                ))}
              </div>
            )}
            <span className="youtube_show_more">
              {showDescription ? "Mostrar menos" : "Mostrar mais"}
            </span>
          </div>

          {/* Comments Section */}
          <div className="youtube_comments_section">
            <div
              className="youtube_comments_header"
              onClick={() => setShowComments(!showComments)}
            >
              <h3>{post.comments?.length || 0} comentários</h3>
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
                style={{ transform: showComments ? "rotate(180deg)" : "none" }}
              >
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
              </svg>
            </div>

            {showComments && (
              <>
                <div className="youtube_comment_form">
                  <img src={user.picture} alt="" />
                  <input
                    type="text"
                    placeholder="Adicionar um comentário..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleComment()}
                  />
                  {commentText && (
                    <div className="youtube_comment_actions">
                      <button onClick={() => setCommentText("")}>Cancelar</button>
                      <button className="primary" onClick={handleComment}>
                        Comentar
                      </button>
                    </div>
                  )}
                </div>

                <div className="youtube_comments_list">
                  {post.comments?.map((c, i) => (
                    <div key={i} className="youtube_comment_item">
                      <img src={c.commentBy?.picture} alt="" />
                      <div className="youtube_comment_body">
                        <div className="youtube_comment_header">
                          <span className="youtube_comment_author">
                            {c.commentBy?.first_name} {c.commentBy?.last_name}
                          </span>
                          <Moment fromNow locale="pt-br">{c.commentAt}</Moment>
                        </div>
                        <p>{c.text}</p>
                        <div className="youtube_comment_footer">
                          <button>
                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                              <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
                            </svg>
                            <span>{c.reacts?.length || 0}</span>
                          </button>
                          <button>Responder</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
